import { useEffect } from 'react';
import useStore from 'Hooks/useStore';
import { isNotNullOrUndefined } from 'Util/TypeGuards';
import { assignDriverDetailsToPassenger } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardData/PassengerDetail';
import { RenderRemoveTrailerModal } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/Modals/RemoveTrailerModal';
import { BookingWizardData } from '../../BookingWizardData/BaseBookingWizardData';
import { isVehicleBookingWizardData, VehicleBookingWizardData } from '../../BookingWizardData/VehicleBookingWizardData';
import { useBookingWizardDataStore } from '../../BookingWizardDataStore';
import { WizardErrors } from '../../BookingWizardTypes';

export function useVehicleTabLogic(isCustomer: boolean) {
	const store = useStore();
	const { wizardData, setWizardData, oldWizardData, innerWizardErrors, setInnerWizardErrors } =
		useBookingWizardDataStore();

	useEffect(() => {
		const newData = new BookingWizardData({ ...wizardData });
		if (isVehicleBookingWizardData(newData.innerWizardData)) {
			// Handle Trailer Logic
			// If trailer was previously selected (in oldWizardData) but is now deselected, prompt the user if there's a removal fee
			if (
				!newData.innerWizardData.trailerCheckboxSelected &&
				(oldWizardData?.innerWizardData as VehicleBookingWizardData)?.trailerTypeId !== 'NO_TRAILER' &&
				(wizardData.innerWizardData as VehicleBookingWizardData).trailerTypeId !== 'NO_TRAILER'
			) {
				if ((oldWizardData?.innerWizardData as VehicleBookingWizardData)?.trailerCheckboxSelected) {
					if (
						wizardData.wizardMode === 'ALTERATION' &&
						isNotNullOrUndefined(wizardData.departingCancellationFee) &&
						isNotNullOrUndefined(wizardData.departingCancellationCutoffHours)
					) {
						RenderRemoveTrailerModal(
							wizardData.departureTrip === undefined,
							wizardData.bookingToEdit ?? '',
							wizardData.departingCancellationCutoffHours,
						).then(res => {
							if (isVehicleBookingWizardData(newData.innerWizardData)) {
								if (res) {
									// User confirmed removal
									newData.innerWizardData.trailerTypeId = 'NO_TRAILER';
									newData.innerWizardData.trailerLengthId = '';
									newData.innerWizardData.trailerCheckboxSelected = false;
								} else if (isNotNullOrUndefined(oldWizardData)) {
									// User cancelled, revert to old data
									newData.innerWizardData.trailerTypeId = (
										oldWizardData.innerWizardData as VehicleBookingWizardData
									).trailerTypeId;
									newData.innerWizardData.trailerCheckboxSelected = (
										oldWizardData.innerWizardData as VehicleBookingWizardData
									).trailerCheckboxSelected;
									newData.innerWizardData.trailerLengthId = (
										oldWizardData.innerWizardData as VehicleBookingWizardData
									).trailerLengthId;
								}
								setWizardData(newData);

								const newErrors = { ...(innerWizardErrors as WizardErrors<VehicleBookingWizardData>) };
								delete newErrors.trailerTypeId;
								delete newErrors.trailerLengthId;
								setInnerWizardErrors(newErrors);

								// Un-focus the input after user has finished with the pop-up
								(document.activeElement as HTMLElement).blur();
							}
						});
					}
				} else {
					// No previous selection or not in alteration mode requiring fee check
					newData.innerWizardData.trailerTypeId = 'NO_TRAILER';
					newData.innerWizardData.trailerLengthId = '';
				}
			}

			// Handle Driver Details Auto-Population
			// Automatically fill in driver details from logged-in user profile if fields are empty
			if (store.loggedIn && newData.innerWizardData.driverFirstName === '' && isCustomer) {
				newData.innerWizardData.driverFirstName = store.userData?.firstName ?? '';
				newData.innerWizardData.driverLastName = store.userData?.lastName ?? '';
				newData.innerWizardData.driverPhone = store.userData?.phone ?? '';
				assignDriverDetailsToPassenger(newData);
			}

			setWizardData(newData);
		}
	}, [
		isCustomer,
		setWizardData,
		store.loggedIn,
		store.userData?.firstName,
		store.userData?.lastName,
		store.userData?.phone,
		(wizardData.innerWizardData as VehicleBookingWizardData).trailerCheckboxSelected,
	]);
}
